import createVanilla, { StoreApi } from "zustand/vanilla";
import create, { UseStore } from "zustand";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	NotificationViewModel,
	AvailableNotificationViewModel,
	loadUpdate,
	NotificationsUpdateRequest,
	NotificationsUpdateViewModel,
	dismiss,
	NotificationStatus,
	markAllAsRead,
	deleteRange,
	deleteNotification,
	deleteAll,
	MutedActivityFilterModel,
	listMuteFilters,
	NotificationKind,
	MutedActivityFilterKind,
	VolatileActivitiesClientModel,
	getVolatileActivities
} from "client/Api/Notifications/notifications";
import { persist } from "zustand/middleware";
import { EncryptionSerializer, SafeStateStorage } from "./persistence";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { normalizeCustomActivityTag } from "./stacks";
import { waitForUserConfigurationToInitialize } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { Poller } from "client_legacy/ndaw/poller";
import {
	isNotNil,
	isNotNilOrEmpty,
	debounce
} from "client/Misc/utils";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { hasRequiredLicenseForNotification } from "./hasRequiredLicenseForNotification";

type ActivityFeedUpdateRequest = {
	listRequestParameters: NotificationsUpdateRequest;
	cancellation: AbortController;
};

export type ActivityWithAvailableFrom = {
	activity: NotificationViewModel;
	availableFrom: Date;
};

export type OrderedReadonlyArray<T> = ReadonlyArray<T> & {
	__hasCorrectOrder: true;
};

export type ActivityFeedStoreData = {
	isLoading: boolean;
	isInitialized: boolean;
	didInitiallyLoadFromBackend: boolean;
	amountOfUnreadNotifications: number | undefined;
	allActivitiesIncludingHiddenToAUser: OrderedReadonlyArray<NotificationViewModel>;
	activities: OrderedReadonlyArray<AvailableNotificationViewModel>;
	pendingDismissedIds: ReadonlyArray<number>;
	invokedActivity: AvailableNotificationViewModel | undefined;
	muteFilters: ReadonlyArray<MutedActivityFilterModel>;
	volatileActivities: VolatileActivitiesClientModel | null;
	loadUpdates(): Promise<void>;
	loadFilters(): Promise<void>;
	dismiss(id: number): void;
	delete(ids: readonly number[]): Promise<void>;
	deleteAll(): Promise<void>;
	setInvokedActivity(activity: AvailableNotificationViewModel): void;
	clearInvokedActivity(): void;
	markAllAsRead(): void;
	startPollingVolatileActivities: () => void;
	stopPollingVolatileActivities: () => void;
};

let activityFeedStoreRehydrationHandlers: PromiseHandlers<void> | undefined;
let activityFeedStoreRehydration: Promise<void> | null = new Promise<void>((resolve, reject) => {
	activityFeedStoreRehydrationHandlers = {resolve, reject};
});

const serializer = new EncryptionSerializer();
const storage = new SafeStateStorage(serializer);

export const activityFeedStore: StoreApi<ActivityFeedStoreData> = createVanilla<ActivityFeedStoreData>(
	persist(
		(set, get) => {
			let volatileActivityPollerPromiseHandlers: PromiseHandlers<Poller> | null = null;
			const waitForVolatileActivityPoller: Promise<Poller> = new Promise(
				(resolve, reject) => {
					volatileActivityPollerPromiseHandlers = {
						resolve, reject
					};
				}
			);

			const initializeUpdatesPoller = (pollingIntervalInSeconds: number): void => {
				const {
					loadUpdates,
					loadFilters
				} = get();

				const poller = new Poller({
					intervalInSeconds: pollingIntervalInSeconds,
					callback: loadUpdates
				});

				poller.start();
				loadFilters();
			};

			const initializeVolatilePoller = (pollingIntervalInSeconds: number): void => {
				if (volatileActivityPollerPromiseHandlers === null) {
					console.error("VolatilePoller's promise handlers are null and not initialized");
				}

				volatileActivityPollerPromiseHandlers?.resolve(
					new Poller({
						intervalInSeconds: pollingIntervalInSeconds,
						callback: fetchVolatileActivities
					})
				);
			};

			waitForUserConfigurationToInitialize()
				.then(userConfiguration => {
					initializeUpdatesPoller(userConfiguration.notificationsListUpdateIntervalInSeconds);
					initializeVolatilePoller(userConfiguration.notificationsListUpdateIntervalInSeconds);
				});

			let loadUpdatesRequest: ActivityFeedUpdateRequest | undefined;
			let loadFiltersCancellation: AbortController | undefined;

			const submitDismissedActivities = async (immediateIds: ReadonlyArray<number>): Promise<void> => {
				const {
					pendingDismissedIds,
					allActivitiesIncludingHiddenToAUser,
					muteFilters,
					loadUpdates
				} = get();

				const idsToDismiss: number[] = [
					...pendingDismissedIds,
					...immediateIds
				];

				const updatedActivitiesIncludingPending: OrderedReadonlyArray<NotificationViewModel> = markActivitiesAsDismissed(
					allActivitiesIncludingHiddenToAUser,
					idsToDismiss
				);

				set({
					...createActivitiesState(updatedActivitiesIncludingPending, muteFilters),
					pendingDismissedIds: []
				});

				try {
					await dismiss(idsToDismiss);
				} finally {
					await loadUpdates();
				}
			};

			const waitForRehydrationToFinish = async (): Promise<void> => {
				if (activityFeedStoreRehydration) {
					try{
						await activityFeedStoreRehydration;
					} catch(e) {
						console.error("Rehydration from browser storage failed. Reloading entire state from the backend.", e);
					} finally {
						// NOTE: rehydration only happens once, so we only need to wait for rehydration to finish once.
						activityFeedStoreRehydration = null;
					}
				}
			};

			const submitWithNoParameters: () => Promise<void> = submitDismissedActivities.bind(undefined, []);

			const debouncedSubmitDismissedActivities: () => Promise<void> = debounce(submitWithNoParameters, 1000);

			async function fetchVolatileActivities(signal: AbortSignal): Promise<void> {
				try {
					const utcNow = new Date().toISOString();
					const result: VolatileActivitiesClientModel = await getVolatileActivities(utcNow, signal);

					set({ volatileActivities: result });
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					set({ volatileActivities: null });
				}
			}

			const result: ActivityFeedStoreData = {
				isLoading: false,
				isInitialized: false,
				didInitiallyLoadFromBackend: false,
				activities: markAsOrdered({alreadyOrderedItems: []}),
				allActivitiesIncludingHiddenToAUser: markAsOrdered({alreadyOrderedItems: []}),
				pendingDismissedIds: [],
				amountOfUnreadNotifications: undefined,
				invokedActivity: undefined,
				muteFilters: [],
				volatileActivities: null,
				startPollingVolatileActivities: () => {

					waitForVolatileActivityPoller
						.then(poller => poller.start());
				},
				stopPollingVolatileActivities: () => {
					waitForVolatileActivityPoller
						.then(poller => poller.stop());
				},
				loadUpdates: async (): Promise<void> => {
					async function fetchItems(request: NotificationsUpdateRequest, signal: AbortSignal): Promise<void> {
						try {
							const update: NotificationsUpdateViewModel = await loadUpdate(request, signal);

							loadUpdatesRequest = undefined;
							set(previous => {
								const allActivities: OrderedReadonlyArray<NotificationViewModel> = applyUpdate(previous.allActivitiesIncludingHiddenToAUser, update);

								return {
									...createActivitiesState(allActivities, previous.muteFilters),
									isLoading: false,
									didInitiallyLoadFromBackend: true
								};
							});

						} catch (e) {
							if (isCancelledError(e)) {
								return;
							}

							loadUpdatesRequest = undefined;
							set({
								isLoading: false,
								didInitiallyLoadFromBackend: true
							});
						}
					}

					await waitForRehydrationToFinish();

					const {
						allActivitiesIncludingHiddenToAUser
					} = get();

					const newRequestParams: NotificationsUpdateRequest = createRequestParams(allActivitiesIncludingHiddenToAUser);

					if (loadUpdatesRequest) {
						if (isTheSame(loadUpdatesRequest.listRequestParameters, newRequestParams)) {
							return Promise.resolve();
						}

						loadUpdatesRequest.cancellation.abort();
					}

					loadUpdatesRequest = {
						listRequestParameters: newRequestParams,
						cancellation: new AbortController()
					};

					await fetchItems(newRequestParams, loadUpdatesRequest.cancellation.signal);
				},
				loadFilters: async (): Promise<void> => {
					async function fetchFilters(signal: AbortSignal): Promise<void> {
						try {
							const {
								filters: muteFilters
							} = await listMuteFilters(signal);

							loadFiltersCancellation = undefined;
							set(previous => {
								return {
									...createActivitiesState(previous.allActivitiesIncludingHiddenToAUser, muteFilters),
									muteFilters
								};
							});

						} catch (e) {
							if (isCancelledError(e)) {
								return;
							}

							loadFiltersCancellation = undefined;
						}
					}

					await waitForRehydrationToFinish();

					if (loadFiltersCancellation) {
						loadFiltersCancellation.abort();
					}

					loadFiltersCancellation = new AbortController();

					await fetchFilters(loadFiltersCancellation.signal);
				},
				dismiss: (id: number): void => {
					debouncedSubmitDismissedActivities();

					set(state => {
						const {
							pendingDismissedIds
						} = state;
						if (pendingDismissedIds.some(a => a === id)) {
							return {
								pendingDismissedIds
							};
						}

						const copy: number[] = [...pendingDismissedIds];
						copy.push(id);
						return { pendingDismissedIds: copy };
					});
				},
				delete: async (ids: readonly number[]): Promise<void> => {
					if (ids.length === 1) {
						await deleteNotification(ids[0]);
					} else {
						await deleteRange(ids);
					}

					set(
						state => {
							const allActivities: OrderedReadonlyArray<NotificationViewModel> = markAsOrdered({
								alreadyOrderedItems: state.allActivitiesIncludingHiddenToAUser
									.map(x =>
										x.status !== NotificationStatus.Pending && x.canBeDeletedByUser && ids.indexOf(x.id) !== -1
											? markActivityAsRemoved(x)
											: x
									)
							});

							return createActivitiesState(allActivities, state.muteFilters);
						}
					);

					const { loadUpdates } = get();
					loadUpdates();
				},
				deleteAll: async (): Promise<void> => {
					try {
						await deleteAll();

						set(
							state => {
								const allActivities: OrderedReadonlyArray<NotificationViewModel> = markAsOrdered({
									alreadyOrderedItems: state.allActivitiesIncludingHiddenToAUser
										.map(x =>
											x.status !== NotificationStatus.Pending && x.canBeDeletedByUser
												? markActivityAsRemoved(x)
												: x
											)
								});

								return createActivitiesState(allActivities, state.muteFilters);
							}
						);

					} finally {
						const { loadUpdates } = get();
						loadUpdates();
					}
				},
				setInvokedActivity: (activity: AvailableNotificationViewModel): void => {
					set({
						invokedActivity: activity
					});
				},
				clearInvokedActivity: (): void => {
					set({
						invokedActivity: undefined
					});
				},
				markAllAsRead: (): void => {
					const {
						loadUpdates
					} = get();

					markAllAsRead()
						.then(loadUpdates);
				}
			};

			return result;
		},
		{
			name: "ActivityFeed",
			getStorage: () => storage,
			onRehydrateStorage: () => {
				return (_, error) => {
					if (activityFeedStoreRehydrationHandlers) {
						if (error) {
							activityFeedStoreRehydrationHandlers.reject(error);
						} else {
							activityFeedStoreRehydrationHandlers.resolve();
						}
					}
				};
			},
			serialize: state => serializer.serializeState(state),
			deserialize: ((str: string) => serializer.deserializeState(str)) as any,
			version: 2
		}
	)
);

export const useActivityFeedStore: UseStore<ActivityFeedStoreData> = create(activityFeedStore);

function markActivitiesAsDismissed(
	allActivities: OrderedReadonlyArray<NotificationViewModel>,
	dismissedIds: ReadonlyArray<number>
): OrderedReadonlyArray<NotificationViewModel> {
	const updatedActivities: ReadonlyArray<NotificationViewModel> = allActivities.map((a: NotificationViewModel): NotificationViewModel => {
		return a.status === NotificationStatus.Available && dismissedIds.some(i => i === a.id)
			// The backend marks an activity as read when it is dismissed, we match this behavior
			? { ...a, isDismissed: true, isRead: true, version: INTERPOLATED_UPDATE_ACTIVITY_VERSION }
			: a;
	});

	return markAsOrdered({alreadyOrderedItems: updatedActivities});
}

function markActivityAsRemoved(notification: NotificationViewModel): NotificationViewModel {
	if (notification.status === NotificationStatus.Removed)
	{
		return notification;
	}

	return {
		...notification,
		availableUntil: new Date().toISOString(),
		status: NotificationStatus.Removed,
		version: INTERPOLATED_UPDATE_ACTIVITY_VERSION
	} as NotificationViewModel;
}

function createRequestParams(
	activities: OrderedReadonlyArray<NotificationViewModel>
): NotificationsUpdateRequest {
	if (activities.length === 0) {
		return {
			latestLoadedVersion: null,
			earliestAvailableFromToCheckForStatusChange: null,
			earliestAvailableUntilToCheckForStatusChange: null
		};
	}

	const latestLoadedVersion: number = Math.max(...activities.map(a => a.version));
	const availableFromsOfPendingActivities: Date[] = activities
		.filter(a => a.status === NotificationStatus.Pending)
		.map(a => parseDtoDate(a.availableFrom));

	let earliestAvailableFromToCheckForStatusChange: Date | null;

	if (availableFromsOfPendingActivities.length === 0) {
		earliestAvailableFromToCheckForStatusChange = null;
	} else {
		const smallestAvailableFromOfPendingActivities: Date = new Date(
			Math.min(
				...availableFromsOfPendingActivities.map(d => d.getTime())
			)
		);

		earliestAvailableFromToCheckForStatusChange = smallestAvailableFromOfPendingActivities;
	}

	const availableUntilsOfAvailableActivities: Date[] = filterAvailableActivities(activities)
		.map(a => a.availableUntil)
		.filter(isNotNil)
		.map(until => parseDtoDate(until));

	let earliestAvailableUntilToCheckForStatusChange: Date | null;

	if (availableUntilsOfAvailableActivities.length === 0) {
		earliestAvailableUntilToCheckForStatusChange = null;
	} else {
		const smallestAvailableUntilOfAvailableActivities = new Date(
			Math.min(
				...availableUntilsOfAvailableActivities.map(d => d.getTime())
			)
		);

		earliestAvailableUntilToCheckForStatusChange = smallestAvailableUntilOfAvailableActivities;
	}

	return {
		latestLoadedVersion,
		earliestAvailableFromToCheckForStatusChange: earliestAvailableFromToCheckForStatusChange,
		earliestAvailableUntilToCheckForStatusChange: earliestAvailableUntilToCheckForStatusChange
	};
}

export function createActivitiesState(
	allActivities: OrderedReadonlyArray<NotificationViewModel>,
	muteFilters: ReadonlyArray<MutedActivityFilterModel>
): Pick<
	ActivityFeedStoreData,
		| "activities"
		| "allActivitiesIncludingHiddenToAUser"
		| "amountOfUnreadNotifications"
		| "muteFilters"
>
{
	const availableActivities: OrderedReadonlyArray<AvailableNotificationViewModel> =
		filterAvailableActivities(allActivities);

	const unmutedActivities: AvailableNotificationViewModel[] = availableActivities.filter(
		activity => !muteFilters.some(f => isActivityMutedByFilter(activity, f))
	);

	const orderedUnmutedActivities: OrderedReadonlyArray<AvailableNotificationViewModel> = markAsOrdered({
		alreadyOrderedItems: unmutedActivities
	});

	const licensedActivities: OrderedReadonlyArray<AvailableNotificationViewModel> = filterLicensedActivities(orderedUnmutedActivities);

	return {
		allActivitiesIncludingHiddenToAUser: allActivities,
		activities: licensedActivities,
		amountOfUnreadNotifications: calculateUnreadCount(licensedActivities),
		muteFilters
	};
}

function calculateUnreadCount(
	unmutedAvailableActivities: ReadonlyArray<AvailableNotificationViewModel>
): number {
	const unreadUnmutedActivities: ReadonlyArray<AvailableNotificationViewModel> = unmutedAvailableActivities
		.filter(n => !n.isRead);

	return unreadUnmutedActivities.length;
}

function filterAvailableActivities(
	allActivities: OrderedReadonlyArray<NotificationViewModel>
): OrderedReadonlyArray<AvailableNotificationViewModel> {
	const availableActivities: ReadonlyArray<AvailableNotificationViewModel> = allActivities
		.filter(a => a.status === NotificationStatus.Available) as ReadonlyArray<AvailableNotificationViewModel>;

	return markAsOrdered({alreadyOrderedItems: availableActivities});
}

function filterLicensedActivities(
	allActivities: OrderedReadonlyArray<AvailableNotificationViewModel>
): OrderedReadonlyArray<AvailableNotificationViewModel> {
	const licensedActivities: ReadonlyArray<AvailableNotificationViewModel> = allActivities
		.filter(hasRequiredLicenseForNotification);

	return markAsOrdered({alreadyOrderedItems: licensedActivities});
}

function isActivityMutedByFilter(activity: AvailableNotificationViewModel, filter: MutedActivityFilterModel): boolean {
	switch (filter.kind) {
		case MutedActivityFilterKind.ByActivityKind:
			return activity.kind === filter.activityKind;
		case MutedActivityFilterKind.ByCustomActivityTag:
			return activity.kind === NotificationKind.Custom
				&& isNotNilOrEmpty(activity.data.tag)
				&& normalizeCustomActivityTag(activity.data.tag) === normalizeCustomActivityTag(filter.tag);
		case MutedActivityFilterKind.Appointment:
		case MutedActivityFilterKind.DailyBriefingEmail:
		case MutedActivityFilterKind.WeeklyBriefingEmail:
			return false;
		case MutedActivityFilterKind.CustomActivityWithoutTag:
			return activity.kind === NotificationKind.Custom
				&& !activity.data.tag;
		default:
			throw new UnreachableCaseError(filter);
	}
}

function isTheSame(request1: NotificationsUpdateRequest, request2: NotificationsUpdateRequest): boolean {
	return request1.latestLoadedVersion === request2.latestLoadedVersion
		&& request1.earliestAvailableFromToCheckForStatusChange?.getTime() === request2.earliestAvailableFromToCheckForStatusChange?.getTime()
		&& request1.earliestAvailableUntilToCheckForStatusChange?.getTime() === request2.earliestAvailableUntilToCheckForStatusChange?.getTime();
}

function applyUpdate(
	previousState: ReadonlyArray<NotificationViewModel>,
	update: NotificationsUpdateViewModel
): OrderedReadonlyArray<NotificationViewModel> {

	const {
		patch
	} = update;

	const earliestAvailableFrom: Date = parseDtoDate(update.earliestAvailableFrom);

	const result: ActivityWithAvailableFrom[] = convertToActivityWithAvailableFromCollection(previousState)
		.filter(x => x.availableFrom >= earliestAvailableFrom);

	for (const newActivity of patch) {
		const existingRecordIndex: number = result.findIndex(a => a.activity.id === newActivity.id);
		if (existingRecordIndex !== -1) {
			result.splice(existingRecordIndex, 1, convertToActivityWithAvailableFrom(newActivity));
		} else {
			result.push(convertToActivityWithAvailableFrom(newActivity));
		}
	}

	return sortActivities(result);
}

function convertToActivityWithAvailableFromCollection(activities: ReadonlyArray<NotificationViewModel>): ReadonlyArray<ActivityWithAvailableFrom> {
	return activities.map(convertToActivityWithAvailableFrom);
}

function sortActivities(activities: ReadonlyArray<ActivityWithAvailableFrom>): OrderedReadonlyArray<NotificationViewModel> {
	const result: ActivityWithAvailableFrom[] = [...activities];

	result.sort((a, b) => {
		const timestampDiff: number = b.availableFrom.getTime() - a.availableFrom.getTime();
		if (timestampDiff !== 0) {
			return timestampDiff;
		}
		return a.activity.id - b.activity.id;
	});

	return markAsOrdered({ alreadyOrderedItems: result.map(x => x.activity) });
}

function convertToActivityWithAvailableFrom(activity: NotificationViewModel): ActivityWithAvailableFrom {
	return {
		activity,
		availableFrom: parseDtoDate(activity.availableFrom)
	};
}

// NOTE: this method does not order items, it only marks an array as ordered.
// The actual sorting should happen before calling this method
export function markAsOrdered<T>({alreadyOrderedItems}: {alreadyOrderedItems: ReadonlyArray<T>;}): OrderedReadonlyArray<T> {
	return alreadyOrderedItems as OrderedReadonlyArray<T>;
}

export const INTERPOLATED_UPDATE_ACTIVITY_VERSION = -1;
